import {
    ApiContentSection,
    ApiShopperLandingPageLink,
    ApiProductSource,
    ApiSectionType,
    ApiProductGridLayout,
} from '@storefronts/common-types'
import {
    SectionContainer,
    SectionTitle,
    ProductGridContainer,
    ProductScrollContainer,
    ProductScrollItem,
} from './Shared'
import { useContext, useMemo } from 'react'
import { UserEventDataContext } from '../../../context/UserEventDataContextProvider'
import { ViddyLPContext } from '../../../context/viddyLPContext'
import {
    useProductSort,
    ProductSortProvider,
} from '../../../context/ProductSortContext'
import { useColumnCount } from '../../../context/ProductLayoutContext'
import { ItemsGrid, ItemsGridEmptyState } from '../full-screen/PLPDrawer'
import { ProductControls } from '../../shared/ProductControls'
import {
    ProductFilterPills,
    useFilterState,
} from '../../shared/ProductFilterDialog'
import { ButtonLoadingSpinner } from '../../icons/Icons'
import { getProductHistoryByEventTypes } from '../../../utils/userEventTools'
import {
    sortProducts,
    getSectionDefaultSort,
} from '../../../utils/productSorting'

type PersonalizedPLPContentSection = Extract<
    ApiContentSection,
    { type: ApiSectionType.PERSONALIZED_PLP }
>

const apiProductSourceToLabel = {
    [ApiProductSource.RECENTLY_VIEWED]: 'Recently Viewed',
    [ApiProductSource.ADDED_TO_CART]: 'Previously In Your Cart',
    [ApiProductSource.PURCHASED]: 'Past Purchases',
}

export const PersonalizedPLPSection: React.FC<{
    section: PersonalizedPLPContentSection
    isBelowFold?: boolean
}> = ({ section, isBelowFold = true }) => {
    const defaultSort = getSectionDefaultSort(section)
    return (
        <ProductSortProvider defaultSort={defaultSort}>
            <PersonalizedPLPSectionContent
                section={section}
                isBelowFold={isBelowFold}
            />
        </ProductSortProvider>
    )
}

const PersonalizedPLPSectionContent: React.FC<{
    section: PersonalizedPLPContentSection
    isBelowFold: boolean
}> = ({ section, isBelowFold }) => {
    const { abandonmentStorefrontData, initialized } =
        useContext(UserEventDataContext)
    const { landingPage } = useContext(ViddyLPContext)
    const { sortOption } = useProductSort()
    const columnCount = useColumnCount()
    const filterState = useFilterState()

    const products = useMemo<ApiShopperLandingPageLink[]>(() => {
        if (
            !initialized ||
            !abandonmentStorefrontData?.product_history ||
            !section.productSource
        ) {
            return []
        }
        return getProductHistoryByEventTypes(
            abandonmentStorefrontData,
            section.productSource,
        )
            .map((product) => product.landing_page_link)
            .filter((link): link is ApiShopperLandingPageLink => !!link)
            .slice(0, section.maxProducts)
    }, [
        abandonmentStorefrontData,
        initialized,
        section.maxProducts,
        section.productSource,
    ])

    const filteredProducts = useMemo(() => {
        return filterState.getFilteredProducts(products, landingPage?.discount)
    }, [products, filterState.getFilteredProducts, landingPage?.discount])

    const sortedProducts = useMemo(() => {
        return sortProducts(
            filteredProducts,
            sortOption,
            landingPage?.discount || null,
        )
    }, [filteredProducts, sortOption, landingPage?.discount])

    const productSourceLabel = section.productSource
        ? apiProductSourceToLabel[section.productSource]
        : undefined

    if (products.length === 0 && initialized) {
        // Don't show the section if there are no products available
        return null
    }
    return (
        <SectionContainer>
            {productSourceLabel && (
                <SectionTitle
                    isHorizontalScroll={
                        section.layoutStyle ===
                        ApiProductGridLayout.HORIZONTAL_SCROLL
                    }
                >
                    {productSourceLabel}
                </SectionTitle>
            )}
            {section.layoutStyle !== ApiProductGridLayout.HORIZONTAL_SCROLL && (
                <>
                    <ProductControls
                        products={products}
                        filterState={filterState}
                    />
                    <ProductFilterPills
                        activeFilters={filterState.selectedFilters}
                        removeFilter={filterState.removeFilter}
                        priceRange={filterState.priceRange}
                        removePriceFilter={filterState.removePriceFilter}
                        currencySymbol={landingPage?.client?.currencySymbol}
                    />
                </>
            )}
            {section.layoutStyle === ApiProductGridLayout.HORIZONTAL_SCROLL ? (
                !initialized ? (
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '100%',
                        }}
                    >
                        <ButtonLoadingSpinner color="black" />
                    </div>
                ) : filteredProducts.length === 0 ? (
                    <ItemsGridEmptyState>
                        No products match your filters. Try adjusting your
                        selections.
                    </ItemsGridEmptyState>
                ) : (
                    <ProductScrollContainer>
                        {sortedProducts.map((product, cardIndex) => (
                            <ProductScrollItem key={product.linkHash}>
                                <ItemsGrid
                                    landingPageLinks={[product]}
                                    columnCount={columnCount}
                                    isHorizontalScroll={true}
                                    isBelowFold={isBelowFold || cardIndex > 0}
                                />
                            </ProductScrollItem>
                        ))}
                    </ProductScrollContainer>
                )
            ) : (
                <ProductGridContainer columnCount={columnCount}>
                    {initialized && filteredProducts.length === 0 && (
                        <ItemsGridEmptyState>
                            No products match your filters. Try adjusting your
                            selections.
                        </ItemsGridEmptyState>
                    )}
                    {initialized && (
                        <ItemsGrid
                            landingPageLinks={sortedProducts}
                            columnCount={columnCount}
                            isBelowFold={isBelowFold}
                        />
                    )}
                    {!initialized && (
                        <div
                            style={{
                                gridColumn: '1 / -1',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '100%',
                            }}
                        >
                            <ButtonLoadingSpinner color="black" />
                        </div>
                    )}
                </ProductGridContainer>
            )}
        </SectionContainer>
    )
}
